import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchLiveBoard,
  fetchUserMonth,
  fetchWorkforceUsers,
  formatDuration,
  formatWorkEventLabel,
  thStyle,
  tdStyle,
} from '../api/workforceApi';
import { formatUtcToIst } from '../../tickets/utils/datetime';
import ListSearch, { filterBySearch, USER_SEARCH_KEYS } from '../../../components/ListSearch';
import { TicketDetailLink } from '../../tickets/ticketNav';
import { CategoryPie, WorkloadBar } from '../../../components/module-ui';
import useVisibleInterval from '../../../hooks/useVisibleInterval';

function todayIstDate() {
  // Approximate IST as UTC+5:30 for the date picker default.
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 - now.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
}

function WorkforceUserPage({ refreshKey = 0 }) {
  const [searchParams] = useSearchParams();
  const now = new Date();
  const [board, setBoard] = useState(null);
  const [boardError, setBoardError] = useState(null);
  const mountedRef = useRef(true);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userId, setUserId] = useState(searchParams.get('user') || '');
  const [periodMode, setPeriodMode] = useState('month'); // month | date
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [date, setDate] = useState(todayIstDate());
  const [report, setReport] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadBoard = useCallback(() => {
    fetchLiveBoard()
      .then((data) => {
        if (mountedRef.current) {
          setBoard(data);
          setBoardError(null);
        }
      })
      .catch((e) => {
        if (mountedRef.current) setBoardError(e.response?.data?.detail || 'Failed to load live status');
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    loadBoard();
  }, [refreshKey, loadBoard]);

  useVisibleInterval(loadBoard, 30000);

  useEffect(() => {
    fetchWorkforceUsers().then(setUsers).catch(() => {});
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get('user');
    if (fromUrl) setUserId(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (!userId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    const paramsDate = periodMode === 'date' ? date : undefined;
    const y = periodMode === 'date' && date ? Number(date.slice(0, 4)) : year;
    const m = periodMode === 'date' && date ? Number(date.slice(5, 7)) : month;
    fetchUserMonth(userId, y, m, paramsDate)
      .then((data) => {
        setReport(data);
        if (periodMode === 'date' && data?.days?.length === 1) {
          setSelectedDay(data.days[0].date);
        } else {
          setSelectedDay(null);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [userId, year, month, date, periodMode, refreshKey]);

  const dayDetail = report?.days?.find((d) => d.date === selectedDay);
  const filteredUsers = useMemo(
    () => filterBySearch(users, userSearch, USER_SEARCH_KEYS),
    [users, userSearch],
  );

  const stat = (label, value, color) => (
    <div style={{ padding: '10px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
      <div style={{ fontSize: '12px', color: '#6B7280' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: '700', color }}>{value}</div>
    </div>
  );

  return (
    <div>
      {boardError && !board && <p style={{ color: '#B91C1C', marginBottom: '10px' }}>{boardError}</p>}
      {board && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', marginBottom: '14px' }}>
            {stat('Users', board.total_members, '#111827')}
            {stat('Free', board.free_count, '#059669')}
            {stat('Busy', board.busy_count, '#2563EB')}
            {stat('On Hold', board.on_hold_count, '#D97706')}
          </div>
          <div className="wd-chart-grid" style={{ marginBottom: 14 }}>
            <CategoryPie
              title="Availability"
              data={{
                free: board.free_count || 0,
                busy: board.busy_count || 0,
                on_hold: board.on_hold_count || 0,
              }}
            />
            <WorkloadBar
              title="Busy load sample"
              data={(board.members || board.users || []).slice(0, 12).map((m) => ({
                name: m.name || m.user_name || m.user_id,
                value: m.active_tickets ?? m.ticket_count ?? (m.status === 'busy' ? 1 : 0),
              }))}
            />
          </div>
        </>
      )}

      <h2 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>User Work Analytics</h2>

      <ListSearch
        value={userSearch}
        onChange={setUserSearch}
        placeholder="Search users…"
        resultCount={filteredUsers.length}
        totalCount={users.length}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', padding: '10px', background: '#F9FAFB', borderRadius: '8px' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>User</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)} style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', minWidth: '200px' }}>
            <option value="">Select user…</option>
            {filteredUsers.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name} — {u.email} ({u.role})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Period</label>
          <select
            value={periodMode}
            onChange={(e) => setPeriodMode(e.target.value)}
            style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #D1D5DB' }}
          >
            <option value="month">By month</option>
            <option value="date">By date</option>
          </select>
        </div>
        {periodMode === 'month' ? (
          <>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Year</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', width: '100px' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Month</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #D1D5DB' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <div>
            <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Date (IST)</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ padding: '4px 10px', borderRadius: '8px', border: '1px solid #D1D5DB' }}
            />
          </div>
        )}
      </div>

      {!userId && !loading && (
        <p style={{ color: '#6B7280', fontSize: '13px' }}>
          Select a user and a month or particular date to view their work breakdown.
        </p>
      )}
      {loading && <p style={{ color: '#6B7280' }}>Loading report…</p>}
      {error && <p style={{ color: '#B91C1C' }}>{error}</p>}

      {report && (
        <>
          <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px' }}>
            Showing metrics for <strong>{report.period_label || (periodMode === 'date' ? date : `${year}-${month}`)}</strong>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginBottom: '10px' }}>
            <div style={{ padding: '10px', background: '#EFF6FF', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>Tickets Touched</div>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>{report.tickets_touched}</div>
            </div>
            <div style={{ padding: '10px', background: '#D1FAE5', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>Closed</div>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>{report.tickets_closed}</div>
            </div>
            <div style={{ padding: '10px', background: '#F9FAFB', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>Active Time</div>
              <div style={{ fontSize: '15px', fontWeight: '700' }}>{formatDuration(report.total_active_seconds)}</div>
            </div>
          </div>

          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px' }}>
            {periodMode === 'date' ? 'Day detail' : 'Daily breakdown — click a day for details'}
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Tickets</th>
                  <th style={thStyle}>Events</th>
                  <th style={thStyle}>Active Time</th>
                </tr>
              </thead>
              <tbody>
                {(report.days || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdStyle, color: '#6B7280' }}>No work events in this period.</td>
                  </tr>
                ) : report.days.map((d) => (
                  <tr
                    key={d.date}
                    onClick={() => setSelectedDay(d.date)}
                    style={{
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      background: selectedDay === d.date ? '#EFF6FF' : 'transparent',
                    }}
                  >
                    <td style={tdStyle}>{d.date}</td>
                    <td style={tdStyle}>{d.tickets_count}</td>
                    <td style={tdStyle}>{d.events_count}</td>
                    <td style={tdStyle}>{formatDuration(d.active_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dayDetail && (
            <div style={{ marginTop: '24px', padding: '10px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600' }}>{report.name} — {dayDetail.date}</h4>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
                Tickets:{' '}
                {dayDetail.tickets.map((t) => (
                  <span key={t} style={{ marginRight: '12px' }}>
                    <TicketDetailLink ticketNo={t} style={{ color: '#002FA7' }}>{t}</TicketDetailLink>
                    {dayDetail.ticket_seconds?.[t] != null && (
                      <span style={{ color: '#374151', marginLeft: '4px', fontWeight: '600' }}>
                        ({formatDuration(dayDetail.ticket_seconds[t])})
                      </span>
                    )}
                  </span>
                ))}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ textAlign: 'left', background: '#fff' }}>
                    <th style={thStyle}>Time (IST)</th>
                    <th style={thStyle}>Status / Event</th>
                    <th style={thStyle}>Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {dayDetail.events.map((ev, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={tdStyle}>{formatUtcToIst(ev.timestamp)}</td>
                      <td style={tdStyle}>{formatWorkEventLabel(ev)}</td>
                      <td style={tdStyle}>{ev.ticket_no}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WorkforceUserPage;
